import React, { FormEvent, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import _ from 'lodash';
import { IComboBoxOption, Stack, TextField } from '@fluentui/react';
import {
  default_page_size,
  stackTokens15,
  textFieldStyles300,
} from '../../utils';
import {
  GET_CUSTOMER_CONTRACT_UPDATES,
  ICustomerContractUpdate,
} from '../../utils/CustomerContractUpdate';
import useDebounce from '../../components/hooks/useDebounce';
import { ComboboxWithFilter } from '../../components/parts';
import CustomerContractUpdatesOverview from './components/CustomerContractUpdatesOverview';

interface IPagedCustomerContractUpdate {
  customerContractUpdates: ICustomerContractUpdate[];
  skip: number;
  take: number;
  filter: string;
  initialLoad: boolean;
  showLoadMore: boolean;
  isFiltered: boolean;
  isSortedAsc: boolean;
  sortedField: string;
}

const CustomerContractUpdates = () => {
  const { isAuthenticated } = useAuth0();

  const [pagedState, setPagedState] = useState<IPagedCustomerContractUpdate>({
    customerContractUpdates: [],
    skip: 0,
    take: default_page_size,
    filter: '',
    showLoadMore: true,
    isFiltered: false,
    initialLoad: true,
    isSortedAsc: true,
    sortedField: 'document_date',
  });

  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );

  const debouncedValue = useDebounce(filter, 500);

  useEffect(() => {
    setPagedState(prevState => ({
      ...prevState,
      customerContractUpdates: [],
      skip: 0,
    }));
  }, [filter]);

  const getOrderBy = () => {
    const orderByObject: any = {}; // deliberately kept any as type to index by string
    orderByObject[pagedState.sortedField] = pagedState.isSortedAsc
      ? 'asc'
      : 'desc';
    return orderByObject;
  };

  const getFilters = (filterString: string, status?: string) => {
    const filterArray = filterString.split(' ');

    const filterObject: any = {
      AND: [{ status: { equals: status } }],
    };
    for (let i = 0; i < filterArray.length; i++) {
      const filterValue: any = {
        OR: [
          { document_no: { contains: filterArray[i] } },
          { summary: { contains: filterArray[i] } },
          {
            construction_site: {
              name: { contains: filterArray[i] },
            },
          },
        ],
      };

      filterObject.AND.push(filterValue);
    }

    return filterObject;
  };

  const {
    loading,
    error,
    refetch: refetchCustomerContractUpdates,
  } = useQuery(GET_CUSTOMER_CONTRACT_UPDATES, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: getFilters(debouncedValue, statusFilter),
      take: pagedState.take,
      skip: pagedState.skip,
      orderBy: getOrderBy(),
    },
    onCompleted: (x: any) => {
      if (
        x.findManyCustomerContractUpdates &&
        x.findManyCustomerContractUpdates.length > 0
      ) {
        setPagedState(prevState => ({
          ...prevState,
          customerContractUpdates: pagedState.customerContractUpdates.concat(
            x.findManyCustomerContractUpdates,
          ),
          isFiltered: false,
          initialLoad: false,
          showLoadMore: !(
            x.findManyCustomerContractUpdates.length < default_page_size
          ),
        }));
      } else if (x.findManyCustomerContractUpdates) {
        setPagedState(prevState => ({
          ...prevState,
          initialLoad: false,
          showLoadMore: false,
        }));
      }
    },
    fetchPolicy: 'no-cache',
  });

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue ? newValue.toLowerCase() : '';
    setFilter(filterString);
  };

  const setSorting = (isSortedAsc: boolean, sortedField: string) => {
    clearCustomerContractUpdates();
    setPagedState(prevState => ({
      ...prevState,
      isSortedAsc,
      sortedField,
    }));
  };

  const clearCustomerContractUpdates = () => {
    setPagedState(prevState => ({
      ...prevState,
      customerContractUpdates: [],
      skip: 0,
    }));
  };

  const debouncedScroll = _.debounce(() => {
    setPagedState(prevState => {
      const currentDataLength = prevState.customerContractUpdates.length;
      return {
        ...prevState,
        skip: currentDataLength,
      };
    });
  }, 250);

  if (!isAuthenticated) return <p>Verboden</p>;
  if (error) return <p>Oeps, er ging iets mis...</p>;

  return (
    <Stack tokens={stackTokens15}>
      <h3 style={{ marginBottom: 0 }}>Min- en meerwerken</h3>

      <div style={{ display: 'flex' }}>
        <TextField
          label='Zoeken...'
          value={filter}
          styles={textFieldStyles300}
          onChange={onSearchValueChange}
          style={{ marginRight: 30 }}
        />

        <div style={{ width: 300, marginLeft: 70 }}>
          <ComboboxWithFilter
            label='Status'
            options={[
              { key: 'all', text: 'Alle' },
              { key: 'NEW', text: 'Nieuw' },
              { key: 'CANCELLED', text: 'Geannuleerd' },
              { key: 'INVOICED', text: 'Op factuur' },
              { key: 'COMPLETED', text: 'Afgesloten' },
            ]}
            value={statusFilter}
            multiline={false}
            callBack={(newValue: IComboBoxOption[]) => {
              setPagedState(prevState => ({
                ...prevState,
                customerContractUpdates: [],
              }));
              if (
                newValue &&
                newValue.length > 0 &&
                newValue[0].key !== 'all'
              ) {
                setStatusFilter(newValue[0].key as string);
              } else {
                setStatusFilter(undefined);
              }
            }}
          />
        </div>
      </div>

      <CustomerContractUpdatesOverview
        customerContractUpdates={pagedState.customerContractUpdates}
        setCustomerContractUpdates={setPagedState}
        loading={loading}
        initialLoad={pagedState.initialLoad}
        loadMore={debouncedScroll}
        isSortedAsc={pagedState.isSortedAsc}
        sortedField={pagedState.sortedField}
        setSorting={setSorting}
        showLoadMore={pagedState.showLoadMore}
        refetchCustomerContractUpdates={refetchCustomerContractUpdates}
      />
    </Stack>
  );
};

export default CustomerContractUpdates;
